import type { BlockMosaicCards, MosaicCard } from 'types/block-mosaic-cards';
import React from 'react';
import { Wrap, Button, Paragraph } from '@leafwell/components';
import Figure from './figure';
import HeadingBody from './heading-body';

function getCardRowsStyle(idx: number): string {
  switch (idx) {
    case 0:
    default:
      return 'lg:col-span-2 lg:row-start-2';
    case 1:
      return 'lg:col-span-3 lg:row-start-2';
    case 2:
      return 'lg:col-span-3 lg:row-span-2';
    case 3:
      return 'lg:col-span-2';
    case 4:
      return 'lg:col-span-2';
  }
}

function getCardGridStyle(imageAlignment: string): string {
  switch (imageAlignment) {
    case 'middle':
    default:
      return 'gap-4 pr-6 pb-6';
    case 'corner':
      return 'lg:grid-cols-2';
    case 'icon':
      return 'pr-6 pb-6';
    case 'right':
      return 'pb-0 lg:pb-6 lg:grid-cols-[60%_30%]';
  }
}

function getCardBgColor(cardColor: string): string {
  switch (cardColor) {
    case 'lilac':
    default:
      return 'border-[#f7f4fd] bg-[#f7f4fd]';
    case 'green':
      return 'border-[#ebfcf4] bg-[#ebfcf4]';
  }
}

function getImagesStyle(imageAlignment: string): string {
  switch (imageAlignment) {
    case 'middle':
    default:
      return 'max-w-sm mx-auto';
    case 'corner':
      return 'max-w-60 xl:max-w-80 right-0 bottom-0';
    case 'icon':
      return 'lg:w-12';
    case 'right':
      return 'lg:max-w-96 xl:max-w-120 right-0 bottom-0';
  }
}

const Card: React.FC<MosaicCard & { idx?: number }> = ({
  title,
  content,
  image,
  imageAlignment,
  cardColor,
  button,
  idx,
}) => {
  const cardStyles = [
    'grid rounded-md pl-6 pt-6 pr-6 pb-6 relative',
    getCardBgColor(cardColor),
    getCardRowsStyle(idx),
    getCardGridStyle(imageAlignment),
  ].join(' ');
  const imageStyles = getImagesStyle(imageAlignment);
  const isMiddle = imageAlignment === 'middle' || imageAlignment === 'icon';

  return (
    <article className={cardStyles}>
      <span className={['grid gap-y-8'].join(' ')}>
        <span
          className={[
            imageAlignment === 'icon'
              ? 'flex flex-col lg:flex-row gap-y-8 gap-x-4 items-center'
              : 'grid gap-y-8',
          ].join(' ')}
        >
          {title?.length > 0 && (
            <HeadingBody level="4" title={title} tagName="h3" />
          )}
          {image && (
            <Figure
              {...image}
              className={[
                imageAlignment === 'right' ? 'hidden' : '',
                imageAlignment === 'corner'
                  ? 'block justify-self-end -mr-6 -mb-8 lg:hidden'
                  : '',
                imageStyles,
              ].join(' ')}
            />
          )}
        </span>
        {content && (
          <Paragraph
            dangerouslySetInnerHTML={{ __html: content }}
            className={!isMiddle ? 'self-end' : ''}
            tagName="div"
          />
        )}
      </span>
      {image && !isMiddle && (
        <Figure
          {...image}
          className={[
            imageAlignment === 'corner' ? 'hidden ' : '',
            'lg:block lg:absolute',
            imageStyles,
          ].join(' ')}
        />
      )}
      {button && (
        <Button
          href={button.url}
          label={button.title}
          rounded={true}
          target={button.target}
          variant="secondary"
          className="w-full mt-8 lg:mt-0 lg:w-fit lg:h-fit lg:self-end lg:justify-self-end"
        />
      )}
    </article>
  );
};

const MosaicCards: React.FC<BlockMosaicCards> = ({ label, cards }) => {
  return (
    <Wrap className="lg:grid-cols-5 gap-8">
      {label && <Paragraph className="lg:col-span-2">{label}</Paragraph>}
      {cards?.length > 0 &&
        cards.map((card, idx) => {
          return <Card key={`card-${idx}`} {...{ ...card, idx }} />;
        })}
    </Wrap>
  );
};

export default MosaicCards;
